import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { makeStyles } from '@material-ui/core/styles';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import { Formik } from 'formik';
import * as Yup from 'yup';

import {
  ListItemIcon,
  ListItemSecondaryAction,
  Modal,
  TextField,
  IconButton
} from '@material-ui/core';

import Button from '@material-ui/core/Button/Button';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import {
  grantWalletFeatureAction,
  grantWalletFeatureInitialAction,
  revokeWalletFeatureAction
} from '../redux/actions/client-actions';

import Backdrop from '@material-ui/core/Backdrop/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import EditIcon from '@material-ui/icons/Edit';
import Box from '@material-ui/core/Box';
import { SnackbarComponent } from '../../../../core/utils/components/snackbar-component';
import { SettingRowText } from './setting-row-text';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
    width: '100%',
    backgroundColor: theme.palette.background.paper
  },
  inline: {
    display: 'inline'
  },
  heading: {
    margin: theme.spacing(2)
  },
  margin: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  maringCancel: {
    marginBottom: theme.spacing(2)
  },
  wallet_edit: {
    marginRight: theme.spacing(1)
  },
  fab: {
    width: '100%'
  },
  modal: {
    width: 680,
    backgroundColor: '#fff',
    position: 'absolute',
    borderRadius: 5,
    top: '25%',
    left: 0,
    right: 0,
    margin: '0 auto',
    padding: '0 20px'
  },
  formButtons: {
    marginTop: theme.spacing(2)
  },
  warningText: {
    color: '#ea3323'
  },
  modalActions: {
    marginBottom: '20px',
    textAlign: 'center'
  },
  extendedIcon: {
    marginRight: theme.spacing(1)
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff'
  },
  granted: {
    color: '#4caf50'
  }
}));

function CreateIssuerForm(props) {
  const classes = useStyles();

  const { error, loading } = useSelector((state) => state.client);

  const { errors, touched, handleSubmit, handleChange, handleBlur } = props;

  /**
   * @param {string} name
   */
  const formikProps = (name, initialValue = '') => ({
    name: name,
    onChange: handleChange,
    onBlur: handleBlur,
    error: touched[name] && Boolean(errors[name]),
    helperText: touched[name] ? errors[name] : ''
  });

  return (
    <form className={classes.root} noValidate autoComplete="off" onSubmit={handleSubmit}>
      <TextField
        {...formikProps('publickey')}
        id="outlined-full-width"
        label="Issuer Public Key"
        type="text"
        variant="outlined"
        fullWidth={true}
      />

      <TextField
        {...formikProps('privatekey')}
        id="outlined-full-width"
        label="Issuer Private Key"
        type="text"
        variant="outlined"
        fullWidth={true}
      />

      <div className={classes.formButtons}>
        {!loading && (
          <>
            <Button
              className={classes.margin}
              type="submit"
              variant="contained"
              size="large"
              color="primary"
              fullWidth={true}
            >
              Submit
            </Button>
          </>
        )}

        {error && (
          <div>
            <span className={classes.errorText}>{error.message}</span>
          </div>
        )}
      </div>
    </form>
  );
}

export function WalletClientComponent({ client }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [backdrop, setBackdrop] = useState(false);
  const [snackbar, setSnackbar] = useState(false);
  const [message, setMessage] = useState({
    status: 'success',
    description: 'Permission updated'
  });

  const [open, setOpen] = React.useState(false);
  const handleConfirmationOpen = () => setOpen(true);
  const handleConfirmationClose = () => setOpen(false);

  const showSnackbar = (status = 'success', description = 'Permission updated') => {
    setSnackbar(true);
    setMessage({
      status,
      description
    });
    setBackdrop(false);
  };

  const closeSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbar(false);
  };

  const closeBackdrop = () => {
    setBackdrop(false);
  };

  const grant = () => {
    if (client.issuer_public_key !== '') {
      setBackdrop(true);
      const props = { closeBackdrop, showSnackbar };
      dispatch(grantWalletFeatureAction(client.id, props));
    } else {
      handleConfirmationOpen();
    }
  };

  const revoke = () => {
    setBackdrop(true);
    const props = { closeBackdrop, showSnackbar };
    dispatch(revokeWalletFeatureAction(client.id, props));
  };

  const validationSchema = Yup.object({
    publickey: Yup.string('Enter the issuer public key')
      .required('Issuer public key is required')
      .matches('publickey'),
    privatekey: Yup.string('Enter the issuer private key')
      .required('Issuer private key is required')
      .matches('privatekey')
  });

  /* ISSUER KEY VALIDATION
  const validationSchema = Yup.object({
    publickey: Yup.string('Enter the issuer public key')
      .required('Issuer public key is required')
      .min(56, 'Must be exactly 56 characters')
      .max(56, 'Must be exactly 56 characters')
      .matches(/^[A-Z0-9]+$/, 'No Special characters allowed. Uppercase characters only'),
    privatekey: Yup.string('Enter the issuer private key')
      .required('Issuer private key is required')
      .min(56, 'Must be exactly 56 characters')
      .max(56, 'Must be exactly 56 characters')
      .matches(/^[A-Z0-9]+$/, 'No Special characters allowed. Uppercase characters only')
  });
  */

  const submit = (data, actions) => {
    setBackdrop(true);
    const props = { closeBackdrop, showSnackbar };
    dispatch(grantWalletFeatureInitialAction(client.id, data.publickey, data.privatekey, props));
    handleConfirmationClose();
  };

  const values = { publickey: '', privatekey: '' };

  return (
    <>
      <List className={classes.root}>
        <ListItem>
          <ListItemIcon>
            {client.wallet_feature && <FiberManualRecordIcon className={classes.granted} />}
            {!client.wallet_feature && <FiberManualRecordIcon color="error" />}
          </ListItemIcon>
          <SettingRowText
            title="Driver Payment"
            hint="Enables the driver wallet and driver payments for this client."
          />

          <ListItemSecondaryAction>
            {!client.issuer_public_key !== '' && (
              <IconButton
                className={classes.wallet_edit}
                edge="end"
                aria-label="comments"
                onClick={handleConfirmationOpen}
              >
                <EditIcon />
              </IconButton>
            )}
            {client.wallet_feature && (
              <Button
                type="button"
                variant="outlined"
                size="small"
                color="secondary"
                onClick={revoke}
              >
                Disable
              </Button>
            )}
            {!client.wallet_feature && (
              <Button
                type="button"
                variant="contained"
                size="small"
                color="secondary"
                onClick={grant}
              >
                Enable
              </Button>
            )}
          </ListItemSecondaryAction>
        </ListItem>
      </List>

      <>
        <Backdrop
          className={classes.backdrop}
          open={backdrop}
          invisible={false}
          onClick={closeBackdrop}
        >
          <CircularProgress color="inherit" />
        </Backdrop>

        <SnackbarComponent
          open={snackbar}
          onClose={closeSnackbar}
          severity={message.status}
          message={message.description}
        />
      </>

      <Modal
        aria-labelledby="unstyled-modal-title"
        aria-describedby="unstyled-modal-description"
        open={open}
        onClose={handleConfirmationClose}
        BackdropComponent={Backdrop}
      >
        <Box className={classes.modal}>
          <h2 id="unstyled-modal-title">Driver Payment</h2>

          <Formik initialValues={values} validationSchema={validationSchema} onSubmit={submit}>
            {(props) => <CreateIssuerForm {...props} />}
          </Formik>

          <Button
            className={classes.maringCancel}
            variant="outlined"
            size="large"
            color="secondary"
            fullWidth={true}
            onClick={handleConfirmationClose}
          >
            Cancel
          </Button>
        </Box>
      </Modal>
    </>
  );
}
